import { validateASCII, validateCompound, validateLength, validateURL } from '@paskills/validation';
import { TextInput } from 'lego-on-react';
import block from 'propmods';
import React, { FC } from 'react';
import isEmpty from 'lodash/isEmpty';
import * as keyset from 'i18n/AccountLinking';
import * as keysetCommon from 'i18n/Common';
import i18n from '@yandex-int/i18n';
import formContainer from '../../hocs/form-container';
import { InjectedFormProps } from '../../hocs/types';
import { createOrUpdateOAuthApp, ApiError } from '../../api';
import { UpdateOAuthAppSettings } from '../../model/oauth';
import { Field } from '../Form/Form';
import Link from '../Link/Link';
import Submit from '../Settings/Submit';
import labels from './labels';
import './OAuthAppForm.scss';

const t = i18n(keyset);
const tCommon = i18n(keysetCommon);

const b = block('OAuthAppForm');

interface OuterProps {
    settings: UpdateOAuthAppSettings;
    onUpdate: (_id: string) => void;
    name: string;
}

const EMPTY_FIELD = '*****************';

type Props = InjectedFormProps<UpdateOAuthAppSettings, OuterProps, { rateLimit: string }>;

function validate(params: UpdateOAuthAppSettings) {
    const hasRequiredFields = [
        params.clientId,
        params.authorizationUrl,
        !params.id ? params.clientSecret : true,
        params.tokenUrl,
    ].every(Boolean);

    return hasRequiredFields;
}

const OAuthAppFields: FC<Props> = (props) => {
    const { values, isSubmitting, errors, onChange, isNonValidationError, touched } = props;

    const isValid = props.isValid && validate(values);

    const submitMessage = isNonValidationError
        ? t('error-occured')
        : props.isValid
          ? ''
          : errors.rateLimit || t('error-form');
    const submitSeverity = !props.isValid || isNonValidationError ? 'error' : 'info';
    return (
        <div {...b()}>
            <Field
                required
                label={labels.clientId}
                error={errors.clientId}
                caption={
                    <Link to="https://tools.ietf.org/html/rfc6749#section-2.2" target="_blank">
                        Client Identifier
                    </Link>
                }
                name="clientId"
                disableBodyOffset
            >
                <TextInput theme="normal" size="s" text={values.clientId} onChange={onChange('clientId')} />
            </Field>

            <Field
                required={!values.id}
                label={labels.clientSecret}
                error={errors.clientSecret}
                caption={
                    <Link to="https://tools.ietf.org/html/rfc6749#section-2.3.1" target="_blank">
                        Client Password
                    </Link>
                }
                name="clientSecret"
                disableBodyOffset
            >
                <TextInput
                    theme="normal"
                    size="s"
                    placeholder={values.id && isEmpty(values.clientSecret) ? EMPTY_FIELD : undefined}
                    text={values.clientSecret}
                    onChange={onChange('clientSecret')}
                />
            </Field>

            <Field
                required
                label={labels.authorizationUrl}
                error={errors.authorizationUrl}
                caption={
                    <Link to="https://tools.ietf.org/html/rfc6749#section-3.1" target="_blank">
                        API authorization endpoint
                    </Link>
                }
                name="authorizationUrl"
                disableBodyOffset
            >
                <TextInput
                    theme="normal"
                    size="s"
                    text={values.authorizationUrl}
                    type="url"
                    onChange={onChange('authorizationUrl')}
                />
            </Field>

            <Field
                required
                label={labels.tokenUrl}
                error={errors.tokenUrl}
                caption={
                    <Link to="https://tools.ietf.org/html/rfc6749#section-3.2" target="_blank">
                        Token Endpoint
                    </Link>
                }
                name="tokenUrl"
                disableBodyOffset
            >
                <TextInput theme="normal" size="s" text={values.tokenUrl} type="url" onChange={onChange('tokenUrl')} />
            </Field>

            <Field
                label={labels.refreshTokenUrl}
                error={errors.refreshTokenUrl}
                caption={
                    <Link to="https://tools.ietf.org/html/rfc6749#section-6" target="_blank">
                        Refreshing an Access Token
                    </Link>
                }
                name="refreshTokenUrl"
                disableBodyOffset
            >
                <TextInput
                    theme="normal"
                    size="s"
                    text={values.refreshTokenUrl}
                    type="url"
                    onChange={onChange('refreshTokenUrl')}
                />
            </Field>

            <Field
                label={labels.scope}
                error={errors.scope}
                caption={
                    <Link to="https://tools.ietf.org/html/rfc6749#section-3.3" target="_blank">
                        Access Token Scope
                    </Link>
                }
                name="scope"
                disableBodyOffset
            >
                <TextInput theme="normal" size="s" text={values.scope} onChange={onChange('scope')} />
            </Field>

            {!isEmpty(touched) && values.id && <div {...b('warning')}>{t('warning-settings')}</div>}

            <Submit
                text={tCommon('save')}
                inProgress={isSubmitting}
                disabled={!isValid}
                message={submitMessage}
                severity={submitSeverity}
                size="m"
            />
        </div>
    );
};

const OAuthAppForm = formContainer<OuterProps, UpdateOAuthAppSettings>({
    mapPropsToValues: (props) => ({ ...props.settings }),
    handleSubmit: async (values, { onUpdate, name }, { setErrors }) => {
        try {
            const { id } = await createOrUpdateOAuthApp({ ...values, name });
            onUpdate(id);
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.fields) {
                    // @ts-expect-error legacy
                    await new Promise((resolve) => setErrors(err.fields, resolve));
                }
            } else {
                console.error(err);
            }

            throw err;
        }
    },
    setupValidationSchema: () => {
        return {
            clientId: (value: string) => ({
                clientId: validateCompound(
                    validateASCII(value, labels.clientId) && t('error-ascii-app_id'),
                    validateLength(value, 100, labels.clientId) && t('error-length-app_id'),
                ),
            }),
            clientSecret: (value: string) => ({
                clientSecret: validateCompound(
                    validateASCII(value, labels.clientSecret) && t('error-ascii-app_secret'),
                    validateLength(value, 100, labels.clientSecret) && t('error-length-app_secret'),
                ),
            }),
            authorizationUrl: (value: string) => ({
                authorizationUrl: validateCompound(
                    validateURL(value, true) && t('error-invalid_URL'),
                    validateLength(value, 200, labels.authorizationUrl) && t('error-length-authorisation'),
                ),
            }),
            tokenUrl: (value: string) => ({
                tokenUrl: validateCompound(
                    validateURL(value, true) && t('error-invalid_URL'),
                    validateLength(value, 200, labels.tokenUrl) && t('error-length-token'),
                ),
            }),
            refreshTokenUrl: (value?: string) => ({
                refreshTokenUrl: value
                    ? validateCompound(
                          validateURL(value, true) && t('error-invalid_URL'),
                          validateLength(value, 200, labels.refreshTokenUrl) && t('error-length-refresh-token'),
                      )
                    : '',
            }),
            scope: (value?: string) => ({
                scope: value
                    ? validateCompound(
                          validateASCII(value, labels.scope) && t('error-ascii-activity'),
                          validateLength(value, 500, labels.scope) && t('error-length-activity'),
                      )
                    : '',
            }),
            yandexClientId: (value?: string) => ({
                yandexClientId: value
                    ? validateCompound(
                          validateASCII(value, labels.yandexClientId) && t('error-ascii-yandex_id'),
                          validateLength(value, 100, labels.yandexClientId) && t('error-length-yandex_id'),
                      )
                    : '',
            }),
        };
    },
    showPrompt: false,
})(OAuthAppFields);

export default OAuthAppForm;
